<script setup>
import { ref, reactive, onMounted } from 'vue';

const props = defineProps({
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    pollUrlPrefix: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    newConversationUrl: { type: String, required: true },
    executeActionUrl: { type: String, required: true },
    conversationId: { type: [String, Number], default: null },
    initialMessage: { type: String, default: '' },
});

const messages = ref([]);
const context = ref({});
const currentConversationId = ref(props.conversationId);
const isGenerating = ref(false);
const pollTimer = ref(null);

const executingActions = reactive(new Set());
const completedActions = reactive(new Set());
const failedActions = reactive(new Map());

const loadConversation = async () => {
    try {
        const response = await fetch(props.conversationUrl);
        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = data.messages || [];
        context.value = data.context || {};

        // Restore executed actions state
        if (data.context?.executed_actions) {
            data.context.executed_actions.forEach(action => {
                completedActions.add(action.action_id);
            });
        }
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const sendMessage = async (text) => {
    if (isGenerating.value) return;
    messages.value.push({ role: 'user', content: text });
    isGenerating.value = true;

    try {
        const response = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({ prompt: text }),
        });

        const data = await response.json();
        currentConversationId.value = data.conversation_id;

        if (data.status === 'budget_exceeded') {
            isGenerating.value = false;
            pushFailureMessage(data.status);
            return;
        }

        startPolling();
    } catch (e) {
        console.error('Failed to send message', e);
        isGenerating.value = false;
    }
};

const pushFailureMessage = (status) => {
    messages.value.push({
        role: 'assistant',
        content: status === 'budget_exceeded'
            ? _mft('crm-tom:tom.budgetExceeded')
            : _mft('crm-tom:tom.errorMessage'),
        budgetExceeded: status === 'budget_exceeded',
    });
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    pollTimer.value = setInterval(async () => {
        try {
            const response = await fetch(`${props.pollUrlPrefix}${currentConversationId.value}/poll`);
            const data = await response.json();

            if (data.status === 'completed') {
                stopPolling();
                isGenerating.value = false;

                messages.value.push({
                    role: 'assistant',
                    content: data.message,
                    lead_results: data.lead_results,
                    pending_actions: data.pending_actions,
                });
            } else if (data.status === 'failed' || data.status === 'budget_exceeded') {
                stopPolling();
                isGenerating.value = false;
                pushFailureMessage(data.status);
            }
        } catch (e) {
            console.error('Polling failed', e);
        }
    }, 2000);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
    }
};

const startNewConversation = async () => {
    try {
        const response = await fetch(props.newConversationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
        });

        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = [];
        context.value = {};
        executingActions.clear();
        completedActions.clear();
        failedActions.clear();
    } catch (e) {
        console.error('Failed to create new conversation', e);
    }
};

const executeAction = async (actionId) => {
    if (executingActions.has(actionId) || completedActions.has(actionId)) return;

    executingActions.add(actionId);

    try {
        const response = await fetch(props.executeActionUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({
                conversation_id: currentConversationId.value,
                action_id: actionId,
            }),
        });

        const data = await response.json();

        if (data.success) {
            completedActions.add(actionId);
        } else {
            failedActions.set(actionId, data.error || 'Unknown error');
        }
    } catch (e) {
        failedActions.set(actionId, e.message || 'Network error');
    } finally {
        executingActions.delete(actionId);
    }
};

onMounted(() => {
    loadConversation();
});
</script>

<template>
    <div class="flex flex-col h-screen bg-gray-50">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <div class="flex items-center gap-3">
                <a :href="exitUrl" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                    </svg>
                </a>
                <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full bg-aiAgentRelationshipManager flex items-center justify-center">
                        <ai-agent-relationship-manager-avatar class="w-full h-full" />
                    </div>
                    <div>
                        <h1 class="text-sm font-semibold text-gray-900">
                            <mf-translate domainkey="crm-tom:tom.title"></mf-translate>
                        </h1>
                        <p class="text-xs text-gray-500 mb-0">
                            <mf-translate domainkey="crm-tom:tom.subtitle"></mf-translate>
                        </p>
                    </div>
                </div>
            </div>
            <button-secondary
                @click="startNewConversation"
                size="sm"
            >
                <mf-translate domainkey="crm-tom:tom.newChat"></mf-translate>
            </button-secondary>
        </div>

        <!-- Chat -->
        <div class="flex-1 flex flex-col overflow-hidden">
            <tom-chat
                :messages="messages"
                :is-generating="isGenerating"
                :executing-actions="executingActions"
                :completed-actions="completedActions"
                :failed-actions="failedActions"
                :initial-message="initialMessage"
                @send="sendMessage"
                @execute-action="executeAction"
            ></tom-chat>
        </div>
    </div>
</template>
